import React, { useState, useEffect } from "react";
import {
  FaBars,
  FaAngleDown,
  FaUserFriends,
  FaSignOutAlt,
  FaImage,
  FaTags,
  FaBox,
  FaTruck,
  FaPhone,
  FaInfoCircle,
  FaShareAlt,
  FaChartLine,
  FaNetworkWired,
  FaShieldAlt,
  FaPlus,
  FaListAlt,
  FaCoins,
  FaArrowAltCircleUp,
  FaGlobe,
  FaCreditCard,
  FaAngleRight
} from "react-icons/fa";
import { MdDashboard } from 'react-icons/md';
import axios from "axios";
import { config } from "../../config";
import { useNavigate } from "react-router-dom";

const apiUrl = config.apiUrl;

const Sidebar = ({
  isSidebarOpen,
  selectedMenu,
  setSelectedMenu,
  toggleSidebar
}) => {
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [expandedSubMenu, setExpandedSubMenu] = useState(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const userRole = localStorage.getItem("type");

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    // Find active main menu and sub-menu based on selected menu
    for (const mainItem of menuItems) {
      if (mainItem.subMenus) {
        for (const subItem of mainItem.subMenus) {
          if (subItem.subItems?.some(item => item.key === selectedMenu)) {
            setExpandedMenu(mainItem.key);
            setExpandedSubMenu(subItem.key);
            return;
          }
        }
      }
      
      // Check if it's a direct menu item
      if (mainItem.key === selectedMenu || mainItem.directItem === selectedMenu) {
        setExpandedMenu(mainItem.key);
        return;
      }
    }
  }, [selectedMenu]);

  const handleMainMenuClick = (menuKey) => {
    if (expandedMenu === menuKey) {
      setExpandedMenu(null);
      setExpandedSubMenu(null);
    } else {
      setExpandedMenu(menuKey);
      setExpandedSubMenu(null);
    }
  };

  const handleSubMenuClick = (menuKey, event) => {
    event.stopPropagation();
    if (expandedSubMenu === menuKey) {
      setExpandedSubMenu(null);
    } else {
      setExpandedSubMenu(menuKey);
    }
  };

  const handleMenuItemClick = (menuKey) => {
    setSelectedMenu(menuKey);
    if (isMobile) {
      toggleSidebar();
    }
  };

  const handleDashboardClick = () => {
    setSelectedMenu("dashboard");
    if (isMobile) {
      toggleSidebar();
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        clearLocalStorage();
        navigate("/login");
        return;
      }

      await axios.post(`${apiUrl}/logout`, {}, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });

      clearLocalStorage();
      navigate("/login");
      
    } catch (error) {
      console.error("Logout error:", error);
      clearLocalStorage();
      navigate("/login");
      alert(error.response?.data?.message || "Logged out successfully");
    }
  };

  const clearLocalStorage = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("type");
    localStorage.removeItem("user");
  };

  // Define access rules for each role
  const getAccessibleMenus = () => {
    const roleAccess = {
      admin: {
        dashboard: ["dashboard"],
        products: {
          productsSub: ["createProduct", "productOverview"],
          categoriesSub: ["createCategory", "categoryOverview"]
        },
        customers: ["customerOverview"],
        leads: ["leadOverview"],
        siteManagement: {
          menu: ["createMenu", "menuOverview"],
          logo: ["createLogo", "logoOverview"],
          banner: ["createBanner", "bannerOverview"],
          community: ["createCommunity", "communityOverview"],
          contact: ["createContact", "contactOverview"],
          contactInfo: ["createContactInfo", "contactInfoOverview"],
          social: ["createSocial", "socialOverview"],
          legal: ["createLegal", "legalOverview"]
        },
        payments: {
          advancePay: ["createAdvancePay", "advancePayOverview"],
          paymentMethod: ["createPaymentMethod", "paymentMethodOverview"],
          deliveryCharge: ["createDeliveryCharge", "deliveryChargeOverview"]
        },
        coinSystems: {
          bonusCoins: ["createCoin", "coinOverview"],
          packageCoins: ["createPackage", "packageOverview"],
          packageTnx: ["tnxOverview"]
        },
        pixel: {
          pixelSub: ["createPixel", "PixelOverview"]
        },
        steadfast: {
          steadfastSub: ["createSteadfast", "steadfastOverview"]
        },
        users: ["usersOverview"]
      },
      moderator: {
        dashboard: ["dashboard"],
        products: {
          productsSub: ["createProduct", "productOverview"],
          categoriesSub: ["createCategory", "categoryOverview"]
        },
        customers: ["customerOverview"],
        leads: ["leadOverview"],
        siteManagement: {
          banner: ["createBanner", "bannerOverview"],
          community: ["createCommunity", "communityOverview"],
          contact: ["createContact", "contactOverview"]
        },
        payments: {
          advancePay: ["createAdvancePay", "advancePayOverview"],
          paymentMethod: ["createPaymentMethod", "paymentMethodOverview"]
        },
        coinSystems: {
          bonusCoins: ["createCoin", "coinOverview"]
        },
        steadfast: {
          steadfastSub: ["createSteadfast", "steadfastOverview"]
        }
      },
      user: {
        dashboard: ["dashboard"]
      }
    };

    return roleAccess[userRole] || {};
  };

  const hasAccess = (mainKey, subKey = null, itemKey = null) => {
    const accessibleMenus = getAccessibleMenus();
    
    // For direct menu items (like dashboard, customers, leads, users)
    if (!subKey && !itemKey) {
      if (Array.isArray(accessibleMenus[mainKey])) {
        return accessibleMenus[mainKey].includes(mainKey === "dashboard" ? "dashboard" : accessibleMenus[mainKey][0]);
      } else if (typeof accessibleMenus[mainKey] === 'object') {
        return Object.keys(accessibleMenus[mainKey]).length > 0;
      }
      return false;
    }
    
    if (itemKey && subKey) {
      // Check if specific menu item is accessible
      return accessibleMenus[mainKey]?.[subKey]?.includes(itemKey) || false;
    } else if (subKey) {
      // Check if sub-menu has any accessible items
      return accessibleMenus[mainKey]?.[subKey]?.length > 0 || false;
    } else {
      // Check if main menu has any accessible items
      if (accessibleMenus[mainKey]) {
        if (typeof accessibleMenus[mainKey] === 'object' && !Array.isArray(accessibleMenus[mainKey])) {
          // It's an object with sub-menus
          return Object.keys(accessibleMenus[mainKey]).length > 0;
        } else {
          // It's an array of direct items
          return accessibleMenus[mainKey].length > 0;
        }
      }
      return false;
    }
  };

  // Menu items definition with three-level hierarchy
  const menuItems = [
    {
      title: "Dashboard",
      icon: <MdDashboard size={20} />,
      key: "dashboard",
      directItem: "dashboard"
    },
    {
      title: "Store Management",
      icon: <FaBox size={20} />,
      key: "products",
      subMenus: [
        {
          title: "Products",
          key: "productsSub",
          subItems: [
            { name: "Add Product", key: "createProduct", icon: <FaPlus size={14} /> },
            { name: "View Products", key: "productOverview", icon: <FaListAlt size={14} /> }
          ]
        },
        {
          title: "Categories",
          key: "categoriesSub",
          subItems: [
            { name: "Add Category", key: "createCategory", icon: <FaPlus size={14} /> },
            { name: "View Categories", key: "categoryOverview", icon: <FaListAlt size={14} /> }
          ]
        }
      ]
    },
    {
      title: "Orders",
      icon: <FaUserFriends size={20} />,
      key: "customers",
      directItem: "customerOverview"
    },
    {
      title: "Incomplete Orders",
      icon: <FaUserFriends size={20} />,
      key: "leads",
      directItem: "leadOverview"
    },
    {
      title: "Site Management",
      icon: <FaGlobe size={20} />,
      key: "siteManagement",
      subMenus: [
        {
          title: "Menu",
          key: "menu",
          subItems: [
            { name: "Add Menu", key: "createMenu", icon: <FaPlus size={14} /> },
            { name: "View Menus", key: "menuOverview", icon: <FaListAlt size={14} /> }
          ]
        },
        {
          title: "Website Logo",
          key: "logo",
          subItems: [
            { name: "Add Logo", key: "createLogo", icon: <FaPlus size={14} /> },
            { name: "View Logos", key: "logoOverview", icon: <FaListAlt size={14} /> }
          ]
        },
        {
          title: "Banner",
          key: "banner",
          subItems: [
            { name: "Add Banner", key: "createBanner", icon: <FaPlus size={14} /> },
            { name: "View Banners", key: "bannerOverview", icon: <FaListAlt size={14} /> }
          ]
        },
        {
          title: "Community",
          key: "community",
          subItems: [
            { name: "Add Community", key: "createCommunity", icon: <FaPlus size={14} /> },
            { name: "View Communities", key: "communityOverview", icon: <FaListAlt size={14} /> }
          ]
        },
        {
          title: "Contact",
          key: "contact",
          subItems: [
            { name: "Add Contact", key: "createContact", icon: <FaPlus size={14} /> },
            { name: "View Contacts", key: "contactOverview", icon: <FaListAlt size={14} /> }
          ]
        },
        {
      title: "Contact Info",
      key: "contactInfo",
      subItems: [
        { name: "Add Contact Info", key: "createContactInfo", icon: <FaPlus size={14} /> },
        { name: "View Contact Info", key: "contactInfoOverview", icon: <FaListAlt size={14} /> }
      ]
    },
    {
      title: "Social Links",
      key: "social",
      subItems: [
        { name: "Add Social", key: "createSocial", icon: <FaPlus size={14} /> },
        { name: "View Social Links", key: "socialOverview", icon: <FaListAlt size={14} /> }
      ]
    },
    {
      title: "Legal Pages",
      key: "legal",
      subItems: [
        { name: "Add Legal", key: "createLegal", icon: <FaPlus size={14} /> },
        { name: "View Legal Pages", key: "legalOverview", icon: <FaListAlt size={14} /> }
      ]
    }
  ]
},
{
  title: "Payments",
  icon: <FaCreditCard size={20} />,
  key: "payments",
  subMenus: [
    {
      title: "Advance Pay",
      key: "advancePay",
      subItems: [
        { name: "Add Advance Pay", key: "createAdvancePay", icon: <FaPlus size={14} /> },
        { name: "View Advance Pay", key: "advancePayOverview", icon: <FaListAlt size={14} /> }
      ]
    },
    {
      title: "Payment Method",
      key: "paymentMethod",
      subItems: [
        { name: "Add Payment Method", key: "createPaymentMethod", icon: <FaPlus size={14} /> },
        { name: "View Payment Methods", key: "paymentMethodOverview", icon: <FaListAlt size={14} /> }
      ]
    },
    {
      title: "Delivery Charge",
      key: "deliveryCharge",
      subItems: [
        { name: "Add Delivery", key: "createDeliveryCharge", icon: <FaPlus size={14} /> },
        { name: "View Delivery Charges", key: "deliveryChargeOverview", icon: <FaListAlt size={14} /> }
      ]
    }
  ]
},
{
  title: "Coin Systems",
  icon: <FaCoins size={20} />,
  key: "coinSystems",
  subMenus: [
    {
      title: "Bonus Coins",
      key: "bonusCoins",
      subItems: [
        { name: "Add Coins", key: "createCoin", icon: <FaPlus size={14} /> },
        { name: "View Bonus Coins", key: "coinOverview", icon: <FaListAlt size={14} /> }
      ]
    },
    {
      title: "Package Coins",
      key: "packageCoins",
      subItems: [
        { name: "Add Package", key: "createPackage", icon: <FaPlus size={14} /> },
        { name: "View Packages", key: "packageOverview", icon: <FaListAlt size={14} /> }
      ]
    },
    {
      title: "Package Transactions",
      key: "packageTnx",
      subItems: [
        { name: "View Transactions", key: "tnxOverview", icon: <FaListAlt size={14} /> }
      ]
    }
  ]
},
{
  title: "Tracking Setup",
  icon: <FaChartLine size={20} />,
  key: "pixel",
  subMenus: [
    {
      title: "Facebook",
      key: "pixelSub",
      subItems: [
        { name: "Add Pixel", key: "createPixel", icon: <FaPlus size={14} /> },
        { name: "View Pixels", key: "PixelOverview", icon: <FaListAlt size={14} /> }
      ]
    }
  ]
},
{
  title: "Courier Setup",
  icon: <FaNetworkWired size={20} />,
  key: "steadfast",
  subMenus: [
    {
      title: "Steadfast",
      key: "steadfastSub",
      subItems: [
        { name: "Add Steadfast", key: "createSteadfast", icon: <FaPlus size={14} /> },
        { name: "View Steadfast", key: "steadfastOverview", icon: <FaListAlt size={14} /> }
      ]
    }
  ]
},
{
  title: "Users",
  icon: <FaUserFriends size={20} />,
  key: "users",
  directItem: "usersOverview"
}
];

return (
<div
  className={`bg-gradient-to-b from-blue-800 to-blue-600 text-white fixed h-full top-0 left-0 z-40 pt-28
    ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"} 
    w-64 transition-all duration-300 ease-in-out shadow-xl overflow-y-auto`}
>
  {/* Homepage arrow button */}
  <div 
    className="absolute top-24 right-2 cursor-pointer hover:text-blue-300 transition-colors"
    onClick={() => window.open("/", "_blank")}
    title="Go to Homepage"
  >
    <FaArrowAltCircleUp size={24} className="transform rotate-45" />
  </div>

  <ul className="space-y-2 p-4">
    {menuItems.map((item) => {
      // Check if user has access to this menu
      if (!hasAccess(item.key)) return null;
      
      return (
        <li key={item.key}>
          {item.subMenus ? (
            // Main menu with sub-menus
            <>
              <div
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer
                  ${expandedMenu === item.key ? "bg-blue-700" : "hover:bg-blue-700"}`}
                onClick={() => handleMainMenuClick(item.key)}
              >
                <div className="flex items-center">
                  {item.icon}
                  <span className="ml-3">{item.title}</span>
                </div>
                <FaAngleDown className={`transition-transform duration-200 ${
                  expandedMenu === item.key ? 'rotate-180' : ''
                }`} />
              </div>

              {expandedMenu === item.key && (
                <ul className="ml-4 mt-1 space-y-1">
                  {item.subMenus.map((subItem) => {
                    // Check if user has access to this sub-menu
                    if (!hasAccess(item.key, subItem.key)) return null;
                    
                    return (
                      <li key={subItem.key}>
                        <div
                          className={`flex items-center justify-between p-2 pl-6 rounded-lg cursor-pointer
                            ${expandedSubMenu === subItem.key ? "bg-blue-700" : "hover:bg-blue-700"}`}
                          onClick={(e) => handleSubMenuClick(subItem.key, e)}
                        >
                          <div className="flex items-center">
                            <span className="ml-1">{subItem.title}</span>
                          </div>
                          {subItem.subItems && subItem.subItems.length > 0 && (
                            <FaAngleRight className={`transition-transform duration-200 ${
                              expandedSubMenu === subItem.key ? 'rotate-90' : ''
                            }`} />
                          )}
                        </div>

                        {expandedSubMenu === subItem.key && subItem.subItems && subItem.subItems.length > 0 && (
                          <ul className="ml-8 pl-2 mt-1 space-y-1 border-l-2 border-blue-700">
                            {subItem.subItems.map((menuItem) => {
                              // Check if user has access to this menu item
                              if (!hasAccess(item.key, subItem.key, menuItem.key)) return null;
                              
                              return (
                                <li
                                  key={menuItem.key}
                                  className={`flex items-center p-2 rounded-md cursor-pointer transition-colors
                                    ${selectedMenu === menuItem.key ? "bg-blue-600 font-bold" : "hover:bg-blue-600"}`}
                                  onClick={() => handleMenuItemClick(menuItem.key)}
                                >
                                  {menuItem.icon}
                                  <span className="ml-2">{menuItem.name}</span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          ) : (
            // Single menu item without sub-menus (like Dashboard, Customers, Leads, Users)
            <div
              className={`flex items-center p-3 rounded-lg cursor-pointer
                ${selectedMenu === item.directItem ? "bg-blue-700" : "hover:bg-blue-700"}`}
              onClick={() => {
                if (item.key === "dashboard") {
                  handleDashboardClick();
                } else {
                  handleMenuItemClick(item.directItem);
                }
              }}
            >
              {item.icon}
              <span className="ml-3">{item.title}</span>
            </div>
          )}
        </li>
      );
    })}

    {/* Logout Button */}
    <li>
      <div
        className="flex items-center p-3 rounded-lg cursor-pointer hover:bg-red-700 transition-colors"
        onClick={handleLogout}
      >
        <FaSignOutAlt size={20} />
        <span className="ml-3">Logout</span>
      </div>
    </li>
  </ul>
</div>
);
};

export default Sidebar;